import React from 'react';
import classNames from 'classnames';
import { Check, AlertTriangle } from 'react-feather';

import { Spinner } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import RetryButton from 'components/Practice/shared/RetryButton';

import {
  createRecordingElement,
  createPlayerElement,
  withZiggeo,
} from 'components/ui/ZiggeoVideo/ziggeoHelpers';
import ZiggeoMediaPlayer from 'components/ui/ZiggeoVideo/ZiggeoMediaPlayer';
import SkippedOption from 'components/Practice/Recording/SkippedOption';
import ReportTechnicalDifficultiesMessage from 'components/Practice/Recording/ReportTechnicalDifficultiesMessage';
import trackEvent from 'misc/trackEvent';

/*
  When ziggeoForAllMedia has been enabled for all customers & deprecated,
  this file can be removed, since it will no longer be used (initiated from
  VideoResponseQuestion in
  app/views/learning/questions/shared/_video_response.html.erb).
*/
class VideoEmbed extends React.Component {
  videoRecorderRef = React.createRef();

  componentDidMount() {
    if (this.props.givenAnswer && !this.props.hasZiggeoForAllMedia) {
      createPlayerElement(this.props.givenAnswer, this.props.questionId);
    }
  }

  componentDidUpdate(previousProps) {
    const {
      token,
      saveVideo,
      questionId,
      webcamPlaybackEnabled,
      recordWebcamEvent,
      setUploadFinished,
      setUploadStarted,
      handleBadUploadType,
      hasZiggeoForAllMedia,
    } = this.props;
    if (previousProps.token !== token && !hasZiggeoForAllMedia) {
      createRecordingElement({
        token,
        saveVideo,
        questionId,
        webcamPlaybackEnabled,
        recordWebcamEvent,
        setUploadingComplete: setUploadFinished,
        setUploadStarted,
        handleBadUploadType,
      });
      this.replaceHardcodedTranslations();
    }
  }

  replaceHardcodedTranslations() {
    const { current: videoRecorder } = this.videoRecorderRef;
    const recordButton = videoRecorder.querySelector(
      '.ba-videorecorder-chooser-button-0 > span',
    );
    if (recordButton) {
      recordButton.innerText = this.props.i18n.webcam_recording.record;
    }
    const uploadButton = videoRecorder.querySelector(
      '.ba-videorecorder-chooser-button-1 > span',
    );
    if (uploadButton) {
      uploadButton.innerText = this.props.i18n.webcam_recording.upload;
    }
  }

  tryAgainClicked = () => {
    this.props.loadRecording();
    trackEvent('practice/recording_try_again_clicked', {
      content_type: 'question',
      content_id: this.props.questionId,
    });
  };

  render() {
    const {
      token,
      givenAnswer,
      errorMessage,
      i18n,
      skipRecording,
      finished,
      loadRecording,
      previewing,
      lessonTaking,
      questionId,
      removeAbilityToSkip,
      hasZiggeoForAllMedia,
      apiKey,
      technicalDifficultiesReported,
      showAnswerAlreadySubmitted,
      sendFeedbackError,
    } = this.props;
    const recorderStyles = classNames('VideoEmbed', {
      'VideoEmbed--disabled': finished && !givenAnswer,
    });
    const previewingStyles = classNames({ 'VideoEmbed--disabled': previewing });
    const playerStyles = classNames('VideoEmbed-player', {
      'VideoEmbed--background': !lessonTaking,
    });
    const showSkipOption = !finished && !removeAbilityToSkip;
    const showReportTechnicalDifficulties = removeAbilityToSkip;

    // This can be simplified once we are exclusively on the new skip experience
    const skipOrSendFeedbackOption = () => {
      if (
        !sendFeedbackError &&
        (showSkipOption || showReportTechnicalDifficulties)
      ) {
        return (
          <SkippedOption
            skipRecording={skipRecording}
            disabled={finished || previewing}
            showTechnicalDifficulties
            i18n={i18n}
          />
        );
      }
      return null;
    };

    if (givenAnswer) {
      return (
        <div>
          {hasZiggeoForAllMedia ? (
            <ZiggeoMediaPlayer token={givenAnswer} apiKey={apiKey} />
          ) : (
            <div className={playerStyles}>
              <div className="VideoEmbed-videoPlayer" id={questionId} />
            </div>
          )}
          {!finished && lessonTaking && (
            <div className="Video-redo d-f fw-w jc-sb ai-c pos-r">
              <Text
                className="d-f ai-c"
                variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD_SUCCESS}
              >
                <Check className="mr-4" />
                {i18n.webcam_recording.recording_complete}
              </Text>
              <RetryButton onClick={this.tryAgainClicked} />
            </div>
          )}
          {showAnswerAlreadySubmitted && (
            <Text className="d-f ai-c pull-right" variant={TEXT_VARIANTS.BODY}>
              {t(
                'questions.question.media_recording.response_already_submitted',
              )}
            </Text>
          )}
        </div>
      );
    }

    return (
      <div className={recorderStyles}>
        <div className={previewingStyles}>
          {!token && !previewing && !technicalDifficultiesReported && (
            <div className="VideoEmbed--loading ta-c">
              <Spinner color="darker" />
              <span className="ml-16">{i18n.common.loading}</span>
            </div>
          )}
          {token && (
            <div
              ref={this.videoRecorderRef}
              className="VideoEmbed-videoRecorder"
              id={questionId}
            />
          )}
          {previewing && (
            <div className="VideoEmbed-ziggeoButtonContainer d-f jc-c ai-c">
              <button
                type="button"
                className="ba-videorecorder-chooser-button-0"
              >
                {i18n.webcam_recording.record}
              </button>
              <button
                type="button"
                className="ba-videorecorder-chooser-button-1"
              >
                {i18n.webcam_recording.upload}
              </button>
            </div>
          )}
        </div>
        {skipOrSendFeedbackOption()}
        {(technicalDifficultiesReported || sendFeedbackError) && (
          <ReportTechnicalDifficultiesMessage
            reloadRecording={loadRecording}
            sendFeedbackError={sendFeedbackError}
          />
        )}
        {errorMessage && (
          <div className="ErrorContainer bg-transparent ff-ih d-f ai-c p-0 mt-8 mb-0 pos-r">
            <AlertTriangle className="mr-8" />
            <div className="f-1">
              <Text variant={TEXT_VARIANTS.INSTRUX_ERROR}>
                {`${t(
                  'questions.question.video_response.error',
                )} ${errorMessage}. `}
                {`${t('questions.question.video_response.error_follow_up')} ${t(
                  'common.or',
                )} `}
              </Text>
              <button
                type="button"
                className="u-linkBtn d-ib c-p"
                onClick={loadRecording}
              >
                <Text variant={TEXT_VARIANTS.BODY_LINK}>
                  {t('questions.question.video_response.try_again')}
                </Text>
              </button>
              .
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withZiggeo(VideoEmbed);
